import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithErrorHandling } from "../utils/service";

/**
 * Hook to fetch the top 5 spaces for the authenticated user
 * @returns {UseQueryResult} Query result with top spaces
 */
export const useFetchTopSpaces = () => {
  return useQuery({
    queryKey: ["topSpaces"],
    queryFn: () => fetchWithErrorHandling("/v1/spaces/top"),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: false,
  });
};

/**
 * Hook to fetch all spaces for the authenticated user
 * @returns {UseQueryResult} Query result with spaces
 */
export const useFetchSpaces = () => {
  return useQuery({
    queryKey: ["spaces"],
    queryFn: () => fetchWithErrorHandling("/v1/spaces/list"),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: false,
  });
};

/**
 * Hook to create a new space
 * @returns {UseMutationResult} Mutation result
 */
export const useCreateSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (spaceData) =>
      fetchWithErrorHandling("/v1/spaces/create", {
        method: "POST",
        body: JSON.stringify(spaceData),
      }),
    onSuccess: () => {
      // Invalidate spaces query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["spaces"] });
      // Invalidate top spaces query to refetch the top 5 spaces
      queryClient.invalidateQueries({ queryKey: ["topSpaces"] });
    },
  });
};

/**
 * Hook to update an existing space
 * @returns {UseMutationResult} Mutation result
 */
export const useUpdateSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (space) =>
      fetchWithErrorHandling(`/v1/spaces/${space.uuid}/update`, {
        method: "PUT",
        body: JSON.stringify(space),
      }),
    onSuccess: () => {
      // Invalidate spaces query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["spaces"] });
      // Invalidate top spaces query to refetch the top 5 spaces
      queryClient.invalidateQueries({ queryKey: ["topSpaces"] });
    },
  });
};

export const useSetDefaultSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/spaces/${uuid}/default`, {
        method: "PUT",
      }),
    onSuccess: () => {
      // Invalidate spaces query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["spaces"] });
      // Invalidate top spaces query to refetch the top 5 spaces
      queryClient.invalidateQueries({ queryKey: ["topSpaces"] });
    },
  });
};

/**
 * Enhanced hook to delete a space with additional options
 * @returns {UseMutationResult} Mutation result
 */
export const useDeleteSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params) => {
      const { uuid, target } = params;

      // Construct URL with query parameters for deletion options
      let url = `/v1/spaces/${uuid}/delete`;
      const queryParams = new URLSearchParams();

      if (target) {
        queryParams.append("target", target);
      }

      // Append query parameters if they exist
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      return fetchWithErrorHandling(url, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      // Invalidate spaces query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["spaces"] });
      // Invalidate top spaces query to refetch the top 5 spaces
      queryClient.invalidateQueries({ queryKey: ["topSpaces"] });
      // Also invalidate bookmarks and collections since they might have changed
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });
};
